export function distinct<T, U>(
    array: T[],
    callbackfn: (value: T, index: number, array: T[]) => U,
): T[] {
    const mapped = array.map(callbackfn);
    return array.filter(
        (element, index) =>
            mapped.indexOf(callbackfn(element, index, array)) === index,
    );
}

export function range(from: number, to: number): number[] {
    return [...Array(to - from).keys()].map(i => i + from);
}

export function count<T>(
    array: T[],
    callbackfn: (value: T, index: number, array: T[]) => boolean,
): number {
    return array.reduce(
        (x, y, i, arr) => (callbackfn(y, i, arr) ? x + 1 : x),
        0,
    );
}

export function groupBy<T, R>(array: T[], fn: (x: T) => R): Map<R, T[]> {
    return array.reduce((cumulus: Map<R, T[]>, next: T) => {
        const key = fn(next);
        if (cumulus.has(key)) {
            const group = cumulus.get(key) as T[];
            group.push(next);
        } else {
            cumulus.set(key, [next]);
        }
        return cumulus;
    }, new Map<R, T[]>());
}

export function divide<T>(array: T[], fn: (x: T) => boolean): [T[], T[]] {
    const left = [] as T[];
    const right = [] as T[];
    for(const el of array) {
        if(fn(el)) {
            left.push(el);
        } else {
            right.push(el);
        }
    }
    return [left, right];
}

export function diff<T>(a: T[], b: T[], fn = (x: T, y: T) => x === y): T[] {
    return a.filter(i => !b.some(j => fn(i, j)));
}

export function intersect<T>(
    a: T[],
    b: T[],
    fn = (x: T, y: T) => x === y,
): T[] {
    return a.filter(i => b.some(j => fn(i, j)));
}

export function sum<T>(a: T[], fn: (element: T) => number): number {
    return a.reduce((x, y) => x + fn(y), 0);
}
export function average<T>(a: T[], fn: (element: T) => number): number {
    return a.reduce((start, end, _, array) => start + fn(end) / array.length, 0);
}

export function remove<T>(
    a: T[],
    fn: (element: T, i?: number) => boolean,
): T[] {
    const removed: T[] = [];
    let length = a.length;
    for (let i = 0; i < length; i++) {
        if (fn(a[i], i)) {
            removed.push(...a.splice(i, 1));
            i--;
            length--;
        }
    }
    return removed;
}

export function order<T>(array: T[], ...ords: OrderBy<T>[]): T[] {
    return Array.from(array).sort((x, y) =>
        ords.reduce(
            (cumulus, ord) =>
                cumulus === 0
                    ? !ord.desc
                        ? ord.by(x) - ord.by(y)
                        : ord.by(y) - ord.by(x)
                    : cumulus,
            0,
        ),
    );
}

export interface OrderBy<T> {
    by: (value: T) => number;
    desc?: boolean;
}
